import type { DateTime } from "luxon";

/**
 * Wire types for the support-only "merge duplicate employees" endpoints (`/nurse-merge/*`).
 *
 * The backend calls the record that is KEPT `primary` and the one that is deleted `duplicate`. Those words
 * never reach the screen — the dialog names people — but they are the protocol, so they stay here as-is.
 */

export type MergeSide = "primary" | "duplicate";

export type MergeBlocker =
	| "different_networks"
	| "time_off_overlap"
	| "external_shift_overlap"
	| "pbj_shift_overlap"
	| "login_roles_differ"
	| "support_login"
	| "unhandled_reference";

export type MergeWarning = "recurring_shifts_on_both" | "weekly_preferences_dropped" | "wage_history_dropped";

export type MergeLoginPlan = "no_login" | "keep_primary" | "adopt_duplicate" | "merge_accounts";

export type MergeCandidate = {
	nurseId: number;
	firstName: string;
	lastName: string;
	role: string | null;
	email: string | null;
	phoneNumber: string | null;
	departmentIds: string[];
	activeDepartmentIds: string[];
	hasLogin: boolean;
};

export type MergeCandidatePage = {
	items: MergeCandidate[];
	hasMore: boolean;
};

export type MergeMembership = {
	departmentId: string;
	active: boolean;
	status: string | null;
	statusReason: string | null;
	statusStartDate: DateTime | null;
	statusEndDate: DateTime | null;
	scheduledDeactivationDate: DateTime | null;
};

export type MergeExternalLink = {
	source: string;
	externalId: string;
	syncKey: string;
	departmentId: string | null;
};

export type MergeWageHistoryEntry = {
	id: number;
	wage: number | string;
	wageType: string | null;
	startTime: DateTime;
	endTime: DateTime | null;
	createdAt: DateTime | null;
	updatedAt: DateTime | null;
};

export type MergeNurseSummary = {
	nurseId: number;
	firstName: string;
	lastName: string;
	role: string | null;
	employmentType: string | null;
	phoneNumber: string | null;
	email: string | null;
	userId: string | null;
	departmentIds: string[];
	jobIds: string[];
	memberships: MergeMembership[];
	externalLinks: MergeExternalLink[];
	wageHistory: MergeWageHistoryEntry[];
};

export type MergeDepartmentConflict = {
	departmentId: string;
	primaryActive: boolean;
	duplicateActive: boolean;
};

export type MergeRecurringConflict = {
	departmentId: string;
	primaryActiveCount: number;
	duplicateActiveCount: number;
};

export type MergeShiftOverlap = {
	isActual: boolean;
	primaryShiftId: string;
	duplicateShiftId: string;
	/** ISO timestamps arrive as Luxon `DateTime` — `axiosInstance` rehydrates every ISO-shaped string. */
	primaryStartTime: DateTime;
	primaryEndTime: DateTime;
	duplicateStartTime: DateTime;
	duplicateEndTime: DateTime;
};

export type MergeUnhandledReference = {
	table: string;
	column: string;
	rows: number;
};

export type NurseMergePreview = {
	primary: MergeNurseSummary;
	duplicate: MergeNurseSummary;
	/** `"table.column"` → rows of the deleted record that move to the kept one. */
	movedCounts: Record<string, number>;
	departmentConflicts: MergeDepartmentConflict[];
	recurringConflicts: MergeRecurringConflict[];
	shiftOverlaps: MergeShiftOverlap[];
	unhandledReferences: MergeUnhandledReference[];
	loginPlan: MergeLoginPlan;
	blockers: MergeBlocker[];
	warnings: MergeWarning[];
	version: string;
};

type MergeNurseSummaryWire = Omit<MergeNurseSummary, "wageHistory"> & { wageHistory?: MergeWageHistoryEntry[] };

/** Temporary rolling-deploy shape accepted from a backend that predates recurring/wage preview fields. */
export type NurseMergePreviewWire = Omit<NurseMergePreview, "primary" | "duplicate" | "recurringConflicts"> & {
	primary: MergeNurseSummaryWire;
	duplicate: MergeNurseSummaryWire;
	recurringConflicts?: MergeRecurringConflict[];
};

export const normalizeNurseMergePreview = (preview: NurseMergePreviewWire): NurseMergePreview => ({
	...preview,
	primary: {
		...preview.primary,
		// DEFAULT: rolling-deploy bridge; an older backend has no wage-history preview field.
		wageHistory: preview.primary.wageHistory ?? [],
	},
	duplicate: {
		...preview.duplicate,
		// DEFAULT: rolling-deploy bridge; an older backend has no wage-history preview field.
		wageHistory: preview.duplicate.wageHistory ?? [],
	},
	// DEFAULT: rolling-deploy bridge; an older backend cannot report recurring conflicts.
	recurringConflicts: preview.recurringConflicts ?? [],
});

export type NurseMergeDecisions = {
	departmentChoices: Record<string, MergeSide>;
	recurringChoices: Record<string, MergeSide>;
	overlapKeep: MergeSide | null;
};

export type NurseMergeRequest = {
	primaryNurseId: number;
	duplicateNurseId: number;
	decisions: NurseMergeDecisions;
	version: string;
};

export type NurseMergeResult = {
	mergeLogId: string;
	primaryNurseId: number;
	mergedNurseId: number;
	rowCounts: Record<string, number>;
	retiredUserId: string | null;
};

/** Why two records look like the same person, strongest evidence first (the backend orders them). */
export type SuspicionReason = "same_phone" | "same_email" | "same_name" | "similar_name" | "one_inactive" | "bedside_and_admin";

export type SuspectDepartment = {
	departmentId: string;
	name: string;
	active: boolean;
};

export type SuspectRecord = {
	nurseId: number;
	firstName: string;
	lastName: string;
	email: string | null;
	phoneNumber: string | null;
	hasLogin: boolean;
	/** Only this network's facilities — a record linked into another network lists just the ones here. */
	departments: SuspectDepartment[];
};

/** Two records the backend suggests are one person. `first` always has the lower id. */
export type SuspectedDuplicatePair = {
	first: SuspectRecord;
	second: SuspectRecord;
	reasons: SuspicionReason[];
};

export type SuspectedDuplicatePage = {
	items: SuspectedDuplicatePair[];
	total: number;
	hasMore: boolean;
};

export type DismissSuspectedDuplicateRequest = {
	firstNurseId: number;
	secondNurseId: number;
};
